import { Button } from "@movicoders/movicoders-components";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Dispatch, useEffect, useState } from "react";
import {
  generateBlobFromZip,
  generateZip,
} from "../../../helpers/DownloadHelper";
import { MailControllerViewModel } from "../../../viewmodels/MailControllerViewModel";
import IImage from "../../../interfaces/IImage";
import IText from "../../../interfaces/IText";

type TContestDialogProps = {
  onClose: () => void;
  open: boolean;
  images: any[];
  items: (IImage | IText)[];
  items2: (IImage | IText)[];
  backgroundColor: string[];
  onSuccess: Dispatch<React.SetStateAction<boolean>>;
  onError: Dispatch<React.SetStateAction<boolean>>;
};

export const ContestDialog = ({
  onClose,
  open,
  images,
  items,
  items2,
  backgroundColor,
  onSuccess,
  onError,
}: TContestDialogProps) => {
  const { t } = useTranslation();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  
  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const handleSendContestMail = async () => {
    onSuccess(false);

    const newErrors: typeof errors = {};

    if (!fullName.trim()) {
      newErrors.name = t("errors.required");
    }

    if (!email.trim()) {
      newErrors.email = t("errors.required");
    } else if (!validateEmail(email)) {
      newErrors.email = t("errors.invalidEmail");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const zip = await generateZip(items, items2, backgroundColor, images);

    const zipBlob = await generateBlobFromZip(zip);

    const imageResponse = await fetch(images[0]);

    const blob = await imageResponse.blob();

    const imageFile = new File([blob], "espinillera.jpg", {
      type: "image/jpeg",
    });

    try {
      const mailSentCorrectly =
        await MailControllerViewModel.getInstance().sendContestmail(
          fullName,
          email,
          imageFile,
          zipBlob
        );

      if (mailSentCorrectly) {
        onSuccess(true);
      } else {
        onError(true);
      }

      onClose();
    } catch (error) {
      onError(true);
      console.error(t("errors.sendContestMail"), error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {!images.length ? (
        <Grid container style={{ padding: "20px" }}>
          <Grid item style={{ textAlign: "center", margin: "auto" }}>
            <img
              alt="loading-gif"
              src="https://media.giphy.com/media/WvuTFk2IN7jxoLVDkP/giphy.gif"
              width={200}
              height={200}
            />
            <p>{t("generating.images")}</p>
          </Grid>
        </Grid>
      ) : (
        <>
          <DialogTitle>{t("shinpadTemplateView.contest.title")}</DialogTitle>
          <DialogContent>
            <Grid container gap={2} py={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(errors.name)}>
                  <InputLabel htmlFor="fullname-input">
                    {t("shinpadTemplateView.contest.name")}
                  </InputLabel>
                  <Input
                    id="fullname-input"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                  {errors.name && (
                    <FormHelperText>{errors.name}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={Boolean(errors.email)}>
                  <InputLabel htmlFor="email-input">
                    {t("shinpadTemplateView.contest.email")}
                  </InputLabel>
                  <Input
                    id="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {errors.email && (
                    <FormHelperText>{errors.email}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button text={t("confirmDialog.cancel")} onClick={onClose} />
            <Button text={t("send")} onClick={handleSendContestMail} />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
